import React, { useEffect, useState, useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Area, Brush, } from "recharts";
import { Box, Typography, Button, Menu, MenuItem, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip as MuiTooltip } from "@mui/material";
import moment from "moment";
import { sm_othersDataAPI, nema_unbalanceOthers_currentAPI, nema_unbalanceOthersAPI, nema_unbalanceOthers_kwAPI, harmonics_voltage_others, harmonics_current_others } from "../api/smartmeter";
import { useDarkMode } from "../components/DarkModeContext";
import colors from "../colour";
import LoadingOverlay from "./loadingOverlay";
import { sm_othersDatayesterday, sm_othersDatasamedaylastweek, sm_othersDatalastweekavg, sm_othersDatalastmonthavg, sm_othersDatalastyearavg, accum_max_kw_others, unified_meters_others_max, ranked_component_consumption } from "../api/smartmeter";
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';

const IndividualSection = ({ siteId, selectedDate, timeInterval, }) => {
    const { darkMode } = useDarkMode();
    const [isLoading, setIsLoading] = useState(true);
    const [smOthersData, setsmOthersData] = useState([]);
    const [currentData, currentsmOthersData] = useState([]);
    const [nemaCurrent, nemaCurrentData] = useState([]);
    const [nemaVoltage, nemaVoltageData] = useState([]);
    const [nemaPower, nemaPowerData] = useState([]);
    const [meterComparisonType, setMeterComparisonType] = useState({});
    const [meterComparisonData, setMeterComparisonData] = useState({});
    const [othersAnchorEl, setOthersAnchorEl] = useState(null);
    const othersOpen = Boolean(othersAnchorEl);
    const [selectedMeterForCompare, setSelectedMeterForCompare] = useState(null);
    const [meterAccumMaxData, setMeterAccumMaxData] = useState({});
    const [harmonicsCurrent_others, setHarmonicsCurrent_others] = useState([]);
    const [harmonicsVoltage_others, setHarmonicsVoltage_others] = useState([]);
    const [unifiedMetersOthersMax, setUnifiedMetersOthersMax] = useState([]);
    const [rankedComponentConsumption, setRankedComponentConsumption] = useState([]);
    const [rankedComponentConsumptionPrevMonth, setRankedComponentConsumptionPrevMonth] = useState([]);

    // Interval Setting
    const TIME_INTERVALS = useMemo(
        () => ({
            "5m": { value: 300, label: "5 Minute", interval: "5m" },
            "15m": { value: 900, label: "15 Minute", interval: "15m" },
            "30m": { value: 1800, label: "30 Minutes", interval: "30m" },
            "1h": { value: 3600, label: "1 Hour", interval: "1h" },
        }),
        []
    );

    const TIME_INTERVAL = useMemo(
        () => ({
            "5m": { value: 300, label: "5 Minute", interval: "5m" },
        }),
        []
    );

    useEffect(() => {
        if (!siteId || !selectedDate || !timeInterval) return;

        const formattedDate = moment(selectedDate).format("YYYY-MM-DD");
        const selectedValue = TIME_INTERVALS[timeInterval].value;
        const currenttime = moment().format("YYYY-MM-DD");
        const selectedtime = TIME_INTERVAL[timeInterval]?.value ?? 300;
        const prevMonthDate = moment(selectedDate).subtract(1, 'month').format("YYYY-MM-DD");
        const fetchData = async () => {
            setIsLoading(true);
            try {

                const [sm_OthersData, currentdata, nema_current, nema_voltage, nema_power, harmonics_current_res, harmonics_voltage_res, unified_meters_others_max_res, ranked_component_consumption_res, ranked_component_prev_res] = await Promise.all([
                    sm_othersDataAPI(siteId, formattedDate, selectedValue),
                    sm_othersDataAPI(siteId, currenttime, selectedtime),
                    nema_unbalanceOthers_currentAPI(siteId, formattedDate, selectedtime),
                    nema_unbalanceOthersAPI(siteId, formattedDate, selectedValue),
                    nema_unbalanceOthers_kwAPI(siteId, formattedDate, selectedValue),
                    harmonics_current_others(siteId, formattedDate, selectedValue),
                    harmonics_voltage_others(siteId, formattedDate, selectedValue),
                    unified_meters_others_max(siteId, formattedDate),
                    ranked_component_consumption(siteId, formattedDate),
                    ranked_component_consumption(siteId, prevMonthDate)
                ]);

                setsmOthersData(sm_OthersData?.data ?? []);
                currentsmOthersData(currentdata?.data ?? []);
                nemaCurrentData(nema_current?.data ?? []);
                nemaVoltageData(nema_voltage?.data ?? []);
                nemaPowerData(nema_power?.data ?? []);
                setHarmonicsCurrent_others(harmonics_current_res?.data ?? []);
                setHarmonicsVoltage_others(harmonics_voltage_res?.data ?? []);
                setUnifiedMetersOthersMax(unified_meters_others_max_res?.data ?? []);
                setRankedComponentConsumption(ranked_component_consumption_res?.data ?? []);
                setRankedComponentConsumptionPrevMonth(ranked_component_prev_res?.data ?? null);





            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [siteId, selectedDate, timeInterval, TIME_INTERVALS, TIME_INTERVAL]);


    //check online status of the meter
    function getRecentMeterStatus(meters = []) {
        const now = moment();
        return meters.map(meter => {
            if (!Array.isArray(meter.data)) return false;

            const recentData = meter.data.filter(d =>
                moment(d.recorded_at).isAfter(now.clone().subtract(15, 'minutes'))
            );

            return recentData.some(d => d.total_active_power_kw !== null && !isNaN(d.total_active_power_kw));
        });
    }

    const meterStatusesindividual = getRecentMeterStatus(currentData);
    const [expandedGraphs, setExpandedGraphs] = useState({});
    const handleToggleExpand = (index) => {
        setExpandedGraphs((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    const mergedOthersMeters = smOthersData.map((smMeter) => {
        const nemaMeter = nemaVoltage.find(nema => nema.serial_number === smMeter.serial_number);
        return {
            ...smMeter,
            nemaData: nemaMeter?.data || [],
            nemaDescription: nemaMeter?.description || ''
        };
    });


    //hide the smartmeter graph line
    // 1️⃣ Declare the state once, at the top of your component
    const [visibleLines, setVisibleLines] = useState({});

    // 2️⃣ Function to toggle visibility
    const SM_handleLegendClick = (meterId, dataKey) => {
        setVisibleLines((prev) => {
            const current = prev[meterId]?.[dataKey] ?? false; // default to false if undefined
            return {
                ...prev,
                [meterId]: {
                    ...prev[meterId],
                    [dataKey]: !current, // toggle visibility
                },
            };
        });
    };

    const handleOthersClick = (event) => {
        setOthersAnchorEl(event.currentTarget);
    };

    const handleOthersClose = () => {
        setOthersAnchorEl(null);
    };

    const extractTime = (datetime) => {
        if (!datetime) return '';
        if (datetime.match(/^\d{2}:\d{2}:\d{2}$/)) return datetime;
        if (datetime.match(/^\d{2}:\d{2}$/)) return datetime;

        // If it's a full datetime string, extract just HH:MM:SS or HH:MM
        const parts = datetime.split(' ');
        if (parts.length > 1) {
            const timePart = parts[1];
            // If time has seconds, keep it; if not, add :00 for consistency
            if (timePart.match(/^\d{2}:\d{2}:\d{2}$/)) {
                return timePart;
            } else if (timePart.match(/^\d{2}:\d{2}$/)) {
                return `${timePart}:00`; // Add seconds for consistency
            }
            return timePart;
        }

        return datetime;
    };


    // Clear Others comparison
    const clearMeterComparison = (meterSerial) => {
        setMeterComparisonType(prev => {
            const newState = { ...prev };
            delete newState[meterSerial];
            return newState;
        });
        setMeterComparisonData(prev => {
            const newState = { ...prev };
            delete newState[meterSerial];
            return newState;
        });
        setMeterAccumMaxData(prev => {  // ✅ clear accumMax too
            const newState = { ...prev };
            delete newState[meterSerial];
            return newState;
        });
    };

    // Get Others chart data with comparison
    const getMeterChartData = (meterData, meterSerial) => {
        const chartDataPoints = meterData || [];
        if (!chartDataPoints || chartDataPoints.length === 0) return [];

        // ✅ Handle accumMax — per-meter, uses series[].kw
        const accumMax = meterAccumMaxData[meterSerial];
        if (meterComparisonType[meterSerial] === 'accumMax' && accumMax) {
            const seriesData = accumMax.series ?? [];
            const comparisonByTime = new Map();
            seriesData.forEach(item => {
                const timeKey = extractTime(item.recorded_at);
                if (timeKey && item.kw !== null && item.kw !== undefined) {
                    comparisonByTime.set(timeKey, item.kw); // ✅ kw not total_kw
                }
            });

            return chartDataPoints.map(item => {
                const currentTime = extractTime(item.recorded_at);
                const comparisonValue = comparisonByTime.get(currentTime);
                return {
                    recorded_at: item.recorded_at,
                    total_active_power_kw: item.total_active_power_kw ?? null,
                    ...(comparisonValue !== undefined && { comparison_power_kw: comparisonValue }),
                };
            });
        }

        // existing comparison logic below unchanged...
        const comparisonData = meterComparisonData[meterSerial] || [];
        if (!comparisonData || comparisonData.length === 0) {
            return chartDataPoints.map(item => ({
                recorded_at: item.recorded_at,
                total_active_power_kw: item.total_active_power_kw === null ? null : item.total_active_power_kw,
            }));
        }

        const comparisonByTime = new Map();
        comparisonData.forEach(item => {
            const timeKey = extractTime(item.recorded_at);
            if (timeKey && item.total_active_power_kw !== null && item.total_active_power_kw !== undefined) {
                comparisonByTime.set(timeKey, item.total_active_power_kw);
            }
        });

        return chartDataPoints.map(item => {
            const currentTime = extractTime(item.recorded_at);
            const comparisonValue = comparisonByTime.get(currentTime);
            return {
                recorded_at: item.recorded_at,
                total_active_power_kw: item.total_active_power_kw === null ? null : item.total_active_power_kw,
                ...(comparisonValue !== undefined && { comparison_power_kw: comparisonValue }),
            };
        });
    };

    // Get display name for Others comparison for a specific meter
    const getOthersComparisonLabel = (meterSerial) => {
        const type = meterComparisonType[meterSerial];
        switch (type) {
            case 'yesterday': return 'Yesterday';
            case 'sameDayLastWeek': return 'Same Day Last Week';
            case 'lastWeekAvg': return 'Last Week Average';
            case 'lastMonthAvg': return 'Last Month Average';
            case 'lastYearAvg': return 'Last Year Average';
            default: return '';
        }
    };

    // Get color for Others comparison line
    const getOthersComparisonColor = () => {
        return '#8884d8';
    };

    // Fetch Others comparison data for a specific meter
    const fetchOthersComparisonData = async (type, meterSerial) => {
        try {
            const formattedDate = moment(selectedDate).format("YYYY-MM-DD");
            const selectedValue = 300;
            const selectedvalue = TIME_INTERVALS[timeInterval].value;

            let response;
            switch (type) {
                case 'yesterday':
                    response = await sm_othersDatayesterday(siteId, formattedDate, selectedvalue);
                    break;
                case 'sameDayLastWeek':
                    response = await sm_othersDatasamedaylastweek(siteId, formattedDate, selectedvalue);
                    break;
                case 'lastWeekAvg':
                    response = await sm_othersDatalastweekavg(siteId, formattedDate, selectedvalue);
                    break;
                case 'lastMonthAvg':
                    response = await sm_othersDatalastmonthavg(siteId, formattedDate, selectedvalue);
                    break;
                case 'lastYearAvg':
                    response = await sm_othersDatalastyearavg(siteId, formattedDate, selectedvalue);
                    break;
                case 'accumMax':
                    response = await accum_max_kw_others(siteId, formattedDate, selectedvalue);
                    break;
                default:
                    return;
            }

            // ✅ Handle accumMax separately
            if (type === 'accumMax') {
                const meters = response?.data?.meters ?? {};
                // Store each meter's accumMax data separately
                Object.entries(meters).forEach(([serial, meterData]) => {
                    setMeterAccumMaxData(prev => ({
                        ...prev,
                        [serial]: meterData // { max_demand_kw, series: [...] }
                    }));
                });
                setMeterComparisonData(prev => ({ ...prev, [meterSerial]: [] }));
                return;
            }

            const meterData = response?.data?.find(m => m.serial_number === meterSerial);
            if (meterData?.data && Array.isArray(meterData.data)) {
                setMeterComparisonData(prev => ({ ...prev, [meterSerial]: meterData.data }));
                setMeterAccumMaxData(prev => ({ ...prev, [meterSerial]: null })); // clear accumMax
            } else {
                setMeterComparisonData(prev => ({ ...prev, [meterSerial]: [] }));
            }
        } catch (error) {
            console.error(`Error fetching ${type} comparison data for meter ${meterSerial}:`, error);
        }
    };

    // Handle compare button click for a specific meter
    const handleMeterCompare = (type, meterSerial) => {
        const currentType = meterComparisonType[meterSerial];

        if (currentType === type) {
            // Clear comparison for this meter
            setMeterComparisonType(prev => {
                const newState = { ...prev };
                delete newState[meterSerial];
                return newState;
            });
            setMeterComparisonData(prev => {
                const newState = { ...prev };
                delete newState[meterSerial];
                return newState;
            });
        } else {
            // Set comparison for this meter
            setMeterComparisonType(prev => ({ ...prev, [meterSerial]: type }));
            fetchOthersComparisonData(type, meterSerial);
        }
    };

    const getHourTicks = (data) => {
        if (!data || data.length === 0) return [];

        const sampleItem = data[0];
        const timeKey = 'timestamp' in sampleItem ? 'timestamp'
            : 'recorded_at' in sampleItem ? 'recorded_at'
                : null;

        if (!timeKey) return [];

        const seen = new Set();
        const ticks = [];

        data.forEach(d => {
            const time = d[timeKey];
            if (!time) return;

            const m = moment(time);
            if (!m.isValid()) return;

            // Round down to the hour
            const hourKey = m.clone().startOf('hour').toISOString();

            if (!seen.has(hourKey)) {
                seen.add(hourKey);
                ticks.push(time); // push the actual timestamp closest to the hour
            }
        });

        return ticks;
    };

    const KpiCard = ({ label, value, unit, deltaLabel, current, previous, accentColor, darkMode, colors }) => {
        const hasComparison = previous !== null && previous !== undefined && previous !== 0;
        const delta = hasComparison ? ((current - previous) / Math.abs(previous)) * 100 : null;
        const isUp = delta !== null && delta >= 0;

        return (
            <Box
                sx={{
                    position: 'relative',
                    flex: 1,
                    minWidth: 180,
                    p: 2.5,
                    borderRadius: '14px',
                    background: 'linear-gradient(160deg, #0A0F2C 0%, #0D1642 60%, #10193F 100%)',
                    border: '0.5px solid rgba(120,150,255,0.15)',
                    overflow: 'hidden',
                }}
            >
                {/* Subtle accent glow top-right, echoes the network bg */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -30,
                        right: -30,
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${accentColor}33 0%, transparent 70%)`,
                    }}
                />

                <Typography sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'rgba(255,255,255,0.55)', position: 'relative' }}>
                    {label}
                </Typography>

                <Typography sx={{ fontWeight: 700, fontSize: '1.6rem', color: '#fff', mt: 0.5, position: 'relative' }}>
                    {value}{unit}
                </Typography>

                {hasComparison && (
                    <Typography sx={{ fontSize: '0.75rem', mt: 0.5, display: 'flex', alignItems: 'center', gap: '4px', position: 'relative' }}>
                        <Box
                            component="span"
                            sx={{
                                color: isUp ? '#4ADE80' : '#F87171',
                                fontWeight: 700,
                                display: 'inline-flex',
                                alignItems: 'center',
                            }}
                        >
                            {isUp ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
                        </Box>
                        <Box component="span" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            {deltaLabel || 'vs last month'}
                        </Box>
                    </Typography>
                )}
            </Box>
        );
    };

    const RankChangeIndicator = ({ currentRank, previousRank }) => {
        if (!previousRank || previousRank === currentRank) {
            return <Typography sx={{ color: 'text.disabled', fontSize: '0.85rem' }}>—</Typography>;
        }
        const change = previousRank - currentRank;
        const isUp = change > 0;
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                <Box component="span" sx={{ color: isUp ? '#22A06B' : '#E5484D', fontSize: '0.8rem', lineHeight: 1 }}>
                    {isUp ? '▲' : '▼'}
                </Box>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: isUp ? '#22A06B' : '#E5484D' }}>
                    {Math.abs(change)}
                </Typography>
            </Box>
        );
    };

    const getColumnWidths = (showDate) => showDate
        ? ['9%', '25%', '15%', '15%', '24%', '12%']   // Rank, Component, Value, %, Date&Time, Δ Rank
        : ['10%', '30%', '19%', '20%', '21%'];          // Rank, Component, Value, %, Δ Rank

    const RankingTable = ({ title, data, prevData, valueKey, valueUnit, pctKey, showDate, darkMode, colors }) => {
        const columns = showDate
            ? ['Rank', 'Component', 'Value', 'Percentage', 'Date & Time', 'Δ Rank']
            : ['Rank', 'Component', 'Value', 'Percentage', 'Δ Rank'];

        return (
            <Box sx={{ flex: 1, minWidth: 340 }}>
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        borderRadius: '12px',
                        overflow: 'hidden',
                        border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                        boxShadow: darkMode
                            ? '0 4px 20px rgba(0,0,0,0.35)'
                            : '0 4px 20px rgba(0,0,0,0.06)',
                        backgroundColor: darkMode ? '#12172E' : '#fff',
                    }}
                >
                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                        <colgroup>
                            {getColumnWidths(showDate).map((w, i) => (
                                <col key={i} style={{ width: w }} />
                            ))}
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    sx={{
                                        backgroundColor: darkMode ? '#2A1B54' : '#4C1D95',
                                        py: 1.6,
                                        borderBottom: 'none',
                                    }}
                                >
                                    <Typography sx={{ textAlign: 'center', color: '#fff', fontWeight: 700, fontSize: '1rem', letterSpacing: '0.01em' }}>
                                        {title}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                {columns.map((h) => (
                                    <TableCell
                                        key={h}
                                        align="center"  // ✅ all headers centered, including Component
                                        sx={{
                                            fontSize: '0.62rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.02em',
                                            color: darkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
                                            borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                                            py: 1,
                                            px: 0.5,
                                            backgroundColor: darkMode ? '#12172E' : '#fff',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((item, i) => {
                                const prevItem = prevData?.find(p => p.component === item.component);
                                return (
                                    <TableRow
                                        key={item.component}
                                        sx={{
                                            backgroundColor: i % 2 === 0
                                                ? (darkMode ? 'rgba(255,255,255,0.02)' : '#F7F9FA')
                                                : (darkMode ? 'transparent' : '#fff'),
                                            transition: 'background-color 0.15s ease',
                                            '&:hover': {
                                                backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : '#EEF3F6',
                                            },
                                            '& td': { borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.04)' },
                                            '&:last-child td': { borderBottom: 'none' },
                                        }}
                                    >
                                        <TableCell align="center">
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 22,        // ✅ was 26
                                                    height: 22,        // ✅ was 26
                                                    borderRadius: '50%',
                                                    backgroundColor: item.rank === 1
                                                        ? (darkMode ? 'rgba(139,92,246,0.18)' : '#EDE4FB')
                                                        : (darkMode ? 'rgba(255,255,255,0.06)' : '#EEF1F3'),
                                                    color: item.rank === 1 ? '#7C3AED' : (darkMode ? colors.f2 : colors.f3),
                                                    fontWeight: 700,
                                                    fontSize: '0.75rem',   // ✅ was 0.8rem
                                                }}
                                            >
                                                {item.rank}
                                            </Box>
                                        </TableCell>
                                        <TableCell
                                            align="center"  // ✅ centers the name itself
                                            sx={{
                                                fontWeight: 600,
                                                color: darkMode ? colors.f2 : colors.f3,
                                                maxWidth: 0,
                                            }}
                                        >
                                            <TruncatedCell text={item.component} darkMode={darkMode} colors={colors} />
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, color: darkMode ? colors.f2 : colors.f3, whiteSpace: 'nowrap' }}>
                                            {item[valueKey]} {valueUnit}
                                        </TableCell>
                                        <TableCell align="center" sx={{ color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)' }}>
                                            {item[pctKey] != null ? `${item[pctKey]}%` : '—'}
                                        </TableCell>
                                        {showDate && (
                                            <TableCell align="center" sx={{ color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                                                {item.timestamp ? moment(item.timestamp).format('DD MMM, HH:mm') : '—'}
                                            </TableCell>
                                        )}
                                        <TableCell align="center">
                                            <RankChangeIndicator currentRank={item.rank} previousRank={prevItem?.rank} />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    const TruncatedCell = ({ text, darkMode, colors }) => {
        const textRef = React.useRef(null);
        const [isTruncated, setIsTruncated] = useState(false);

        useEffect(() => {
            if (textRef.current) {
                setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
            }
        }, [text]);

        const content = (
            <Box
                ref={textRef}
                component="span"
                sx={{
                    display: 'inline-block',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'bottom',
                }}
            >
                {text}
            </Box>
        );

        return isTruncated ? (
            <MuiTooltip title={text} arrow placement="top">
                {content}
            </MuiTooltip>
        ) : content;
    };

    return (
        <>

            {/* Graph Section */}
            {isLoading ? (
                <LoadingOverlay />
            ) : (

                <Box
                    sx={{
                        '& > *': { mb: '5rem' },
                        backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : colors.l7,
                        borderRadius: '20px',
                        paddingBottom: '1%',
                        paddingTop: '20px',
                    }}
                >



                    {/* ------------------------------------Individual------------------------------------------ */}
                    <div>
                        {isLoading ? (
                            <LoadingOverlay />
                        ) : smOthersData && smOthersData.length > 0 ? (
                            <Box
                                sx={{
                                    width: '70%',
                                    mx: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: "3vh"
                                }}

                            >
                                {/* <Typography sx={{ textAlign: 'center', color: darkMode ? colors.f2 : colors.f3, fontSize: '1.5rem', fontWeight: 'bold', }}>
                                    Individual Analysis
                                </Typography> */}

                                {rankedComponentConsumption && (
                                    <Box sx={{ width: '100%', mx: 'auto', mb: 6, p: 1 }}>

                                        <Typography align="center" sx={{ color: darkMode ? colors.f2 : colors.f3, fontSize: '1.1rem', opacity: 0.8, mb: 0.5 }}>
                                            Total Consumption of Monitored Component(s) in {moment(rankedComponentConsumption.timestamp, 'MMM YYYY').format('MMMM')}:
                                        </Typography>

                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 0.5 }}>
                                            <Typography sx={{ color: darkMode ? colors.f2 : colors.f3, fontSize: '2.4rem', fontWeight: 800, letterSpacing: '-0.01em' }}>
                                                {rankedComponentConsumption.components_summary?.total_components_consumption_kwh?.toLocaleString()} kWh
                                            </Typography>
                                            <Box
                                                sx={{
                                                    px: 1.2,
                                                    py: 0.4,
                                                    borderRadius: '20px',
                                                    backgroundColor: darkMode ? 'rgba(139,92,246,0.15)' : '#EDE4FB',
                                                }}
                                            >
                                                <Typography sx={{ color: darkMode ? '#B794F6' : '#7C3AED', fontWeight: 700, fontSize: '0.85rem' }}>
                                                    {rankedComponentConsumption.components_summary?.percentage_of_total_load}% of total load
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {(() => {
                                            const currentTotal = rankedComponentConsumption.components_summary?.total_components_consumption_kwh ?? 0;
                                            const prevTotal = rankedComponentConsumptionPrevMonth?.components_summary?.total_components_consumption_kwh ?? 0;
                                            const hasComparison = prevTotal > 0;
                                            const delta = hasComparison ? ((currentTotal - prevTotal) / prevTotal) * 100 : null;
                                            const isUp = delta !== null && delta >= 0;

                                            return hasComparison ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', mb: 5 }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            px: 1.2,
                                                            py: 0.4,
                                                            borderRadius: '20px',
                                                            backgroundColor: isUp ? 'rgba(34,160,107,0.1)' : 'rgba(229,72,77,0.1)',
                                                        }}
                                                    >
                                                        <Box component="span" sx={{ color: isUp ? '#22A06B' : '#E5484D', fontWeight: 700, fontSize: '0.85rem' }}>
                                                            {isUp ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
                                                        </Box>
                                                    </Box>
                                                    <Typography sx={{ color: darkMode ? colors.f2 : colors.f3, opacity: 0.6, fontSize: '0.85rem' }}>
                                                        vs last month
                                                    </Typography>
                                                </Box>
                                            ) : null;
                                        })()}

                                        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                                            <RankingTable
                                                title="Consumption"
                                                data={rankedComponentConsumption.ranked_components || []}
                                                prevData={rankedComponentConsumptionPrevMonth?.ranked_components}
                                                valueKey="consumption_kwh"
                                                valueUnit="kWh"
                                                pctKey="percentage_of_grid"
                                                darkMode={darkMode}
                                                colors={colors}
                                            />
                                            <RankingTable
                                                title="Max Power Drawn"
                                                data={(rankedComponentConsumption.ranked_by_max_power_kw || []).map(item => ({
                                                    ...item,
                                                    max_percentage: rankedComponentConsumption.ranked_by_max_percentage?.find(
                                                        p => p.component === item.component
                                                    )?.max_percentage ?? null,
                                                }))}
                                                prevData={rankedComponentConsumptionPrevMonth?.ranked_by_max_power_kw}
                                                valueKey="max_power_kw"
                                                valueUnit="kW"
                                                pctKey="max_percentage"
                                                showDate={true}
                                                darkMode={darkMode}
                                                colors={colors}
                                            />
                                        </Box>
                                    </Box>
                                )}

                                {mergedOthersMeters.map((meter, index) => {
                                    const nemaMeter = nemaVoltage.find(n => n.serial_number === meter.serial_number);

                                    const maxUnbalance = Number(
                                        Math.max(
                                            ...(nemaMeter?.data.map(d => d.nema_unbalanced_pct) || [0]),
                                            0.5
                                        ).toFixed(2)
                                    );

                                    //find the maximum unbalance pct current data 
                                    const nemaMetercurrent = nemaCurrent.find(n => n.serial_number === meter.serial_number);

                                    const maxUnbalancecurrent = Number(
                                        Math.max(
                                            ...(nemaMetercurrent?.data.map(d => d.nema_unbalanced_pct) || [0]),
                                            0.5
                                        ).toFixed(2)
                                    );

                                    //find the maximum unbalance pct power data 
                                    const nemaMeterpower = nemaPower.find(n => n.serial_number === meter.serial_number);

                                    const maxUnbalancepower = Number(
                                        Math.max(
                                            ...(nemaMeterpower?.data.map(d => d.nema_unbalanced_pct) || [0]),
                                            0.5
                                        ).toFixed(2)
                                    );

                                    // Dynamic voltage min/max for left Y-axis
                                    const voltageValues = meter.nemaData?.flatMap(d => [d.voltage_r, d.voltage_y, d.voltage_b]) || [];
                                    const minVoltage = voltageValues.length > 0 ? Math.min(...voltageValues) : 0;
                                    const maxVoltage = voltageValues.length > 0 ? Math.max(...voltageValues) : 250;
                                    const power = meter.nemaData?.flatMap(d => [d.active_power_a_kw, d.active_power_b_kw, d.active_power_c_kw]) || [];
                                    const meterVisibility = visibleLines[meter.serial_number] || {
                                        voltage_an_v: false,
                                        voltage_bn_v: false,
                                        voltage_cn_v: false,
                                        current_a_a: false,
                                        current_b_a: false,
                                        current_c_a: false,
                                        // active_power_a_kw: false,
                                        // active_power_b_kw: false,
                                        // active_power_c_kw: false,

                                    };
                                    const processedothersData = meter.nemaData.map((d) => ({
                                        ...d,
                                        Unbalanced: d.nema_unbalanced_pct > 1
                                            ? Number(d.nema_unbalanced_pct.toFixed(2))
                                            : null,
                                    }));

                                    const processedothersDatacurrent = nemaMetercurrent?.data.map((d) => ({
                                        ...d,
                                        Unbalanced: d.nema_unbalanced_pct > 10
                                            ? Number(d.nema_unbalanced_pct.toFixed(2))
                                            : null,
                                    }));

                                    const processedothersDatapower = nemaMeterpower?.data.map((d) => ({
                                        ...d,
                                        Unbalanced: d.nema_unbalanced_pct != null && d.nema_unbalanced_pct > 1
                                            ? Number(d.nema_unbalanced_pct.toFixed(2))
                                            : null,
                                    }));

                                    const processedOthersPFData = meter.nemaData?.map((d) => ({
                                        ...d,
                                        total_pf: d.total_pf != null ? Number(Number(d.total_pf).toFixed(2)) : null,
                                        low_pf: d.total_pf != null && d.total_pf < 0.9 ? d.total_pf : null,
                                    })) || [];

                                    const otherKVAdata = meter.nemaData?.map((d) => ({
                                        ...d,
                                        total_apparent_power_kva: d.total_apparent_power_kva != null
                                            ? Number(Number(d.total_apparent_power_kva).toFixed(2))
                                            : null,
                                    })) || [];



                                    // Find the corresponding Harmonics Voltage data for this meter
                                    const harmonicsVoltageMeter = (harmonicsVoltage_others || []).find(n => n.serial_number === meter.serial_number);
                                    const processedHarmonicsVoltage = harmonicsVoltageMeter?.data.map((d) => ({
                                        ...d,
                                        harmonic_voltage_rate_a: d.harmonic_voltage_rate_a !== null ? Number(d.harmonic_voltage_rate_a.toFixed(2)) : null,
                                        harmonic_voltage_rate_b: d.harmonic_voltage_rate_b !== null ? Number(d.harmonic_voltage_rate_b.toFixed(2)) : null,
                                        harmonic_voltage_rate_c: d.harmonic_voltage_rate_c !== null ? Number(d.harmonic_voltage_rate_c.toFixed(2)) : null,
                                    }));

                                    // Find the corresponding Harmonics Current data for this meter
                                    const harmonicsCurrentMeter = (harmonicsCurrent_others || []).find(n => n.serial_number === meter.serial_number);
                                    const processedHarmonicsCurrent = harmonicsCurrentMeter?.data.map((d) => ({
                                        ...d,
                                        harmonic_current_rate_a: d.harmonic_current_rate_a !== null ? Number(d.harmonic_current_rate_a.toFixed(2)) : null,
                                        harmonic_current_rate_b: d.harmonic_current_rate_b !== null ? Number(d.harmonic_current_rate_b.toFixed(2)) : null,
                                        harmonic_current_rate_c: d.harmonic_current_rate_c !== null ? Number(d.harmonic_current_rate_c.toFixed(2)) : null,
                                    }));

                                    // ✅ NEW: Find the corresponding Unified Meters Max data for this meter
                                    const unifiedMeter = (unifiedMetersOthersMax?.components || []).find(
                                        c => c.serial_number === meter.serial_number
                                    );

                                    const unifiedBreakdown = (unifiedMeter?.breakdown || []).map((row) => ({
                                        period: row.period,
                                        power_avg: row.power_kw?.avg ?? null,
                                        power_max: row.power_kw?.max ?? null,
                                        power_min: row.power_kw?.min ?? null,
                                        pct_avg: row.percentage?.avg ?? null,
                                        pct_max: row.percentage?.max ?? null,
                                        pct_min: row.percentage?.min ?? null,
                                    }));

                                    const getHourTicks = (data, timeKey = 'recorded_at') => {
                                        if (!data || data.length === 0) return [];

                                        const seen = new Set();
                                        return data.reduce((ticks, d) => {
                                            const time = d[timeKey];
                                            if (!time || seen.has(time)) return ticks;

                                            const m = moment(time);
                                            if (m.isValid() && m.minute() === 0 && m.second() === 0) {
                                                seen.add(time);
                                                ticks.push(time);
                                            }
                                            return ticks;
                                        }, []);
                                    };


                                    const meterOthersTicks = getHourTicks(meter.data);
                                    const meterNemaTicks = getHourTicks(processedothersData);
                                    const meterCurrentTicks = getHourTicks(processedothersDatacurrent);
                                    const meterPowerTicks = getHourTicks(processedothersDatapower);
                                    const meterPFTicks = getHourTicks(processedOthersPFData);
                                    const meterKVATicks = getHourTicks(otherKVAdata);
                                    const meterHarmonicsVoltageTicks = getHourTicks(processedHarmonicsVoltage);
                                    const meterHarmonicsCurrentTicks = getHourTicks(processedHarmonicsCurrent);

                                    const hasHarmonicsData = (data, keys) =>
                                        data?.some(row => keys.some(key => row[key] !== null));

                                    const meterUnifiedTicks = getHourTicks(unifiedBreakdown, 'period');

                                    // Dynamic domain for power (left axis), same pattern as minVoltage/maxVoltage
                                    const unifiedPowerValues = unifiedBreakdown.flatMap(d => [d.power_avg, d.power_max, d.power_min]).filter(v => v !== null);
                                    const minUnifiedPower = unifiedPowerValues.length > 0 ? Math.min(...unifiedPowerValues) : 0;
                                    const maxUnifiedPower = unifiedPowerValues.length > 0 ? Math.max(...unifiedPowerValues) : 1;

                                    const maxUnifiedPct = Number(
                                        Math.max(
                                            ...unifiedBreakdown.map(d => d.pct_max).filter(v => v !== null),
                                            0.5
                                        ).toFixed(2)
                                    );

                                    const currentMeter = currentData.find(c => c.serial_number === meter.serial_number);

                                    const latestSignalStrength = (() => {
                                        const data = currentMeter?.data;
                                        if (!data?.length) return null;
                                        for (let i = data.length - 1; i >= 0; i--) {
                                            if (data[i].signal_strength != null) return data[i].signal_strength;
                                        }
                                        return null;
                                    })();

                                    const processedSignalData = meter.data?.map((d) => ({
                                        ...d,
                                        signal_strength: d.signal_strength != null ? -Math.abs(d.signal_strength) : null,
                                    })) || [];

                                    return (
                                        <Box
                                            key={index}
                                            sx={{
                                                width: '100%',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '2.5%', // space between text and toggle
                                                    marginBottom: '1%', // keeps the spacing from other elements (replaces gutterBottom)
                                                }}
                                            >

                                                <Typography align="center" gutterBottom sx={{ fontWeight: 'bold', color: darkMode ? colors.f2 : colors.f3, fontSize: '1.2rem' }}>
                                                    Smart Meter: {meter.serial_number}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        width: '1.2vw',
                                                        height: '1.5vh',
                                                        borderRadius: '3vw',
                                                        backgroundColor: meterStatusesindividual[index] ? 'green' : 'red',
                                                        position: 'relative',
                                                        transition: 'background-color 0.3s',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '0 1.06%',
                                                        mt: "-0.4%"
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: '0.5vw',
                                                            height: '0.5vw',
                                                            borderRadius: '50%',
                                                            backgroundColor: 'white',
                                                            position: 'absolute',
                                                            left: meterStatusesindividual[index] ? '52%' : '10%',
                                                            transition: 'left 0.3s',
                                                            mt: "-0.5%"
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                            {/* Description + Signal Strength row */}
                                            <Box
                                                sx={{
                                                    height: "auto",
                                                    width: "auto",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    flexDirection: "row",
                                                    gap: "3%",
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                <Typography
                                                    align="center"
                                                    gutterBottom
                                                    sx={{
                                                        color: darkMode ? colors.f2 : colors.f3,
                                                        fontSize: '1.1rem',
                                                        display: "flex",
                                                        alignItems: "center",
                                                        margin: 0,
                                                    }}
                                                >
                                                    Description: {meter.description}
                                                </Typography>

                                                {/* Signal Strength - from currentData (current-time data), not the 15-min meter.data */}
                                                {latestSignalStrength != null && (
                                                    <Typography
                                                        align="center"
                                                        gutterBottom
                                                        sx={{
                                                            color: darkMode ? colors.f2 : colors.f3,
                                                            fontSize: '1.1rem',
                                                            display: "flex",
                                                            alignItems: "center",
                                                            margin: 0,
                                                            pl: "3%",
                                                            borderLeft: `1px solid ${darkMode ? colors.f2 : colors.f3}33`,
                                                        }}
                                                    >
                                                        <SignalCellularAltIcon
                                                            sx={{
                                                                fontSize: '1.1rem',
                                                                mr: 0.5,
                                                                color:
                                                                    latestSignalStrength <= 60
                                                                        ? '#4caf50'   // strong: green
                                                                        : latestSignalStrength <= 80
                                                                            ? '#ff9800'   // medium: orange
                                                                            : '#f44336',  // weak: red
                                                            }}
                                                        />
                                                        {`-${latestSignalStrength} dBm`}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {/* Main Graph */}
                                            {meter.data && meter.data.length > 0 ? (
                                                <Box sx={{ width: '100%', height: 500 }}>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <LineChart data={getMeterChartData(meter?.data, meter.serial_number)}>
                                                            <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? colors.f2 : colors.f1} />
                                                            <XAxis
                                                                dataKey="recorded_at"
                                                                ticks={meterOthersTicks}
                                                                interval={0}
                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                            />
                                                            <YAxis
                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                label={{
                                                                    value: 'Power (kW)',
                                                                    angle: -90,
                                                                    position: 'insideLeft',
                                                                    offset: 10,
                                                                    fill: darkMode ? colors.f2 : colors.f3,
                                                                }}
                                                            />
                                                            <Tooltip
                                                                formatter={(value, name) => {
                                                                    if (typeof value === 'number') return [value.toFixed(2), name];
                                                                    return [value, name];
                                                                }}
                                                                labelFormatter={(label) => `Time: ${moment(label).format('DD-MM-YYYY HH:mm')}`}
                                                                contentStyle={{
                                                                    backgroundColor: darkMode ? 'rgba(0, 0, 25, 0.8)' : colors.f2,
                                                                    color: darkMode ? colors.f2 : colors.f3,
                                                                }}
                                                            />
                                                            <Legend />

                                                            {/* Actual Individual Line */}
                                                            <Line
                                                                type="monotone"
                                                                dataKey="total_active_power_kw"
                                                                stroke={darkMode ? colors.g1 : colors.g8}
                                                                strokeWidth={2}
                                                                dot={{ r: 2, fill: darkMode ? colors.g1 : colors.g8 }}
                                                                name="Power (kW)"
                                                                connectNulls={true}
                                                            />

                                                            {/* Comparison Line */}
                                                            {(meterComparisonData[meter.serial_number]?.length > 0 ||
                                                                meterAccumMaxData[meter.serial_number] !== null &&
                                                                meterAccumMaxData[meter.serial_number] !== undefined) && (
                                                                    <Line
                                                                        type="monotone"
                                                                        dataKey="comparison_power_kw"
                                                                        stroke={getOthersComparisonColor()}
                                                                        strokeWidth={2}
                                                                        dot={false}
                                                                        name={getOthersComparisonLabel(meter.serial_number)}
                                                                        connectNulls={true}
                                                                    />
                                                                )}
                                                        </LineChart>
                                                    </ResponsiveContainer>
                                                </Box>
                                            ) : (
                                                <Typography align="center" color="text.secondary">
                                                    No data available for graph.
                                                </Typography>
                                            )}
                                            {/* Expand/Collapse Button */}
                                            <Box
                                                sx={{
                                                    width: '80%',
                                                    mx: 'auto',
                                                    mt: 5,
                                                    mb: 2,
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    gap: 2,
                                                }}
                                            >
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => handleToggleExpand(index)}
                                                    sx={{
                                                        backgroundColor: darkMode ? colors.d4 : colors.l4,
                                                        width: "160px",
                                                        height: "50px",
                                                        fontSize: "14px",
                                                        position: "relative",
                                                        zIndex: 1,
                                                        color: colors.f2,
                                                    }}
                                                >
                                                    {expandedGraphs[index] ? 'Collapse Details' : 'Expand Details'}
                                                </Button>

                                                {/* Others Compare Button */}
                                                <Button
                                                    id="others-compare-button"
                                                    variant="outlined"
                                                    onClick={(event) => {
                                                        setOthersAnchorEl(event.currentTarget);
                                                        // Store which meter is being compared
                                                        setSelectedMeterForCompare(meter.serial_number);
                                                    }}
                                                    sx={{
                                                        color: darkMode ? colors.f2 : colors.f3,
                                                        borderColor: darkMode ? colors.f2 : colors.f3,
                                                        '&:hover': {
                                                            borderColor: darkMode ? colors.g4 : colors.g1,
                                                            backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
                                                        }
                                                    }}
                                                >
                                                    Compare {meterComparisonType[meter.serial_number] && `(${getOthersComparisonLabel(meter.serial_number)})`}
                                                </Button>

                                                <Menu
                                                    anchorEl={othersAnchorEl}
                                                    open={othersOpen}
                                                    onClose={handleOthersClose}
                                                    anchorOrigin={{
                                                        vertical: 'bottom',
                                                        horizontal: 'left',
                                                    }}
                                                    transformOrigin={{
                                                        vertical: 'top',
                                                        horizontal: 'left',
                                                    }}
                                                    PaperProps={{
                                                        elevation: 3,
                                                        sx: {
                                                            mt: 1,
                                                            minWidth: 220,
                                                            borderRadius: 2,
                                                            overflow: 'hidden',
                                                            backgroundColor: darkMode ? colors.bg : 'white',
                                                            color: darkMode ? colors.f2 : colors.f3,
                                                            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                                                        }
                                                    }}
                                                >
                                                    {/* Clear Comparison */}
                                                    {selectedMeterForCompare && meterComparisonType[selectedMeterForCompare] && (
                                                        <>
                                                            <MenuItem
                                                                onClick={() => {
                                                                    clearMeterComparison(selectedMeterForCompare);
                                                                    handleOthersClose();
                                                                }}
                                                                sx={{
                                                                    px: 2,
                                                                    py: 1,
                                                                    color: 'error.main',
                                                                    '&:hover': {
                                                                        backgroundColor: 'rgba(255,0,0,0.08)',
                                                                    }
                                                                }}
                                                            >
                                                                Clear Comparison
                                                            </MenuItem>

                                                            <Divider />
                                                        </>
                                                    )}

                                                    {/* Options */}
                                                    {[
                                                        { label: 'Yesterday', value: 'yesterday' },
                                                        { label: 'Same Day Last Week', value: 'sameDayLastWeek' },
                                                        { label: 'Last Week Average', value: 'lastWeekAvg' },
                                                        { label: 'Last Month Average', value: 'lastMonthAvg' },
                                                        { label: 'Last Year Average', value: 'lastYearAvg' },
                                                        { label: 'Accumulated Max', value: 'accumMax' },
                                                    ].map((item) => {
                                                        const isSelected = selectedMeterForCompare && meterComparisonType[selectedMeterForCompare] === item.value;

                                                        return (
                                                            <MenuItem
                                                                key={item.value}
                                                                onClick={() => {
                                                                    handleMeterCompare(item.value, selectedMeterForCompare);
                                                                    handleOthersClose();
                                                                }}
                                                                sx={{
                                                                    px: 2,
                                                                    py: 1,
                                                                    borderLeft: isSelected
                                                                        ? `3px solid ${darkMode ? colors.g4 : '#1976d2'}`
                                                                        : '3px solid transparent',
                                                                    backgroundColor: isSelected
                                                                        ? (darkMode
                                                                            ? 'rgba(255,255,255,0.06)'
                                                                            : 'rgba(25,118,210,0.06)')
                                                                        : 'transparent',
                                                                    '&:hover': {
                                                                        backgroundColor: darkMode
                                                                            ? 'rgba(255,255,255,0.1)'
                                                                            : 'rgba(0,0,0,0.05)',
                                                                    },
                                                                }}
                                                            >
                                                                {item.label}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </Menu>
                                            </Box>

                                            {/* Show NEMA details only if expanded */}
                                            {expandedGraphs[index] && (

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 3,
                                                        mx: 'auto',
                                                        width: '100%',
                                                        mt: 2,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            mt: 3,
                                                            p: 2,
                                                            backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : '#f9f9f967',
                                                            borderRadius: '12px',
                                                            boxShadow: darkMode
                                                                ? '0 0 10px rgba(255,255,255,0.1)'
                                                                : '0 0 10px rgba(0,0,0,0.1)',
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', mt: 4 }}>
                                                            {/* Signal Strength graph */}
                                                            {meter.data?.some(d => d.signal_strength != null) && (
                                                                <Box sx={{ width: '48%', height: 350, mb: 6 }}>
                                                                    <Typography
                                                                        variant="subtitle1"
                                                                        sx={{
                                                                            mb: 1,
                                                                            fontWeight: 600,
                                                                            textAlign: 'center',
                                                                            color: darkMode ? colors.f2 : colors.f3
                                                                        }}
                                                                    >
                                                                        Signal Strength (dBm)
                                                                    </Typography>
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <LineChart data={processedSignalData}>
                                                                            <CartesianGrid strokeDasharray="3 3" />
                                                                            <XAxis
                                                                                dataKey="recorded_at"
                                                                                ticks={meterOthersTicks}
                                                                                interval={0}
                                                                                tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                                angle={-65}
                                                                                textAnchor="end"
                                                                                height={60}
                                                                            />
                                                                            <YAxis
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                                label={{
                                                                                    value: 'Signal Strength (dBm)',
                                                                                    angle: -90,
                                                                                    position: 'insideLeft',
                                                                                    style: { fontSize: '0.7vw' },
                                                                                }}
                                                                            />
                                                                            <Tooltip
                                                                                formatter={(v) => [v.toFixed(2), 'Signal Strength (dBm)']}
                                                                                labelFormatter={(label) =>
                                                                                    `Time: ${moment(label).format('DD-MM-YYYY HH:mm')} `
                                                                                }
                                                                                contentStyle={{
                                                                                    backgroundColor: darkMode ? 'rgba(0, 0, 25, 0.8)' : colors.f2,
                                                                                    color: darkMode ? colors.f2 : colors.f3,
                                                                                }}
                                                                            />
                                                                            <Legend verticalAlign="bottom" height={36} />
                                                                            <Line
                                                                                type="monotone"
                                                                                dataKey="signal_strength"
                                                                                stroke={darkMode ? colors.g1 : colors.g8}
                                                                                strokeWidth={2}
                                                                                dot={{ r: 2, fill: darkMode ? colors.g2 : colors.g10 }}
                                                                                animationDuration={1000}
                                                                                name="Signal Strength (dBm)"
                                                                                connectNulls
                                                                            />
                                                                        </LineChart>
                                                                    </ResponsiveContainer>
                                                                </Box>
                                                            )}

                                                            {/* Voltage Unbalance Graph */}
                                                            <Box sx={{ width: '48%', height: 350, mb: 6 }}>
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    sx={{
                                                                        mb: 1,
                                                                        fontWeight: 600,
                                                                        textAlign: 'center',
                                                                        color: darkMode ? colors.f2 : colors.f3
                                                                    }}
                                                                >
                                                                    Phase difference (V)
                                                                </Typography>
                                                                {processedothersData?.length > 0 ? (
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <ComposedChart data={processedothersData}>
                                                                            <CartesianGrid strokeDasharray="3 3" />

                                                                            <XAxis
                                                                                dataKey="recorded_at"
                                                                                ticks={meterNemaTicks}
                                                                                interval={0}
                                                                                tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                                angle={-65}
                                                                                textAnchor="end"
                                                                                height={60}
                                                                            />

                                                                            <YAxis
                                                                                yAxisId="left"
                                                                                domain={[minVoltage, maxVoltage + 5]}
                                                                                label={{
                                                                                    value: 'Voltage (V)',
                                                                                    angle: -90,
                                                                                    position: 'insideLeft',
                                                                                    style: { fontSize: '0.7vw' },
                                                                                }}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                            />

                                                                            <YAxis
                                                                                yAxisId="right"
                                                                                orientation="right"
                                                                                domain={[0, maxUnbalance + 0.1]}
                                                                                label={{
                                                                                    value: 'Unbalanced (%)',
                                                                                    angle: -90,
                                                                                    position: 'insideRight',
                                                                                    style: { fontSize: '0.7vw' },
                                                                                }}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                            />

                                                                            <Tooltip
                                                                                formatter={(v, name) => [v.toFixed(2), name]}
                                                                                labelFormatter={(label) =>
                                                                                    `Time: ${moment(label).format('DD-MM-YYYY HH:mm')}`
                                                                                }
                                                                            />
                                                                            <Legend
                                                                                verticalAlign="bottom"
                                                                                height={36}
                                                                                wrapperStyle={{ fontSize: '0.7vw' }}
                                                                                onClick={(e) => SM_handleLegendClick(meter.serial_number, e.dataKey)}
                                                                                formatter={(value) => (value === 'Unbalanced' ? null : value)}
                                                                            />

                                                                            <Area
                                                                                yAxisId="right"
                                                                                type="monotone"
                                                                                dataKey="nema_unbalanced_pct"
                                                                                name="NEMA Unbalanced (%)"
                                                                                fill="#74b800"
                                                                                stroke="#74b800"
                                                                                strokeWidth={2}
                                                                                fillOpacity={0.3}
                                                                            />

                                                                            <Area
                                                                                yAxisId="right"
                                                                                type="monotone"
                                                                                dataKey="Unbalanced"
                                                                                name=""
                                                                                fill="red"
                                                                                stroke="red"
                                                                                strokeWidth={2}
                                                                                fillOpacity={0.5}
                                                                                connectNulls={false}
                                                                                legendType="none"
                                                                            />

                                                                            <Line
                                                                                yAxisId="left"
                                                                                type="monotone"
                                                                                dataKey="voltage_an_v"
                                                                                name="Voltage AN (V)"
                                                                                stroke="#ff7300"
                                                                                strokeWidth={2}
                                                                                dot={false}
                                                                                connectNulls
                                                                                opacity={meterVisibility.voltage_an_v === false ? 0 : 1}
                                                                            />
                                                                            <Line
                                                                                yAxisId="left"
                                                                                type="monotone"
                                                                                dataKey="voltage_bn_v"
                                                                                name="Voltage BN (V)"
                                                                                stroke="#ffc658"
                                                                                strokeWidth={2}
                                                                                dot={false}
                                                                                connectNulls
                                                                                opacity={meterVisibility.voltage_bn_v === false ? 0 : 1}
                                                                            />
                                                                            <Line
                                                                                yAxisId="left"
                                                                                type="monotone"
                                                                                dataKey="voltage_cn_v"
                                                                                name="Voltage CN (V)"
                                                                                stroke="#6394cc"
                                                                                strokeWidth={2}
                                                                                dot={false}
                                                                                connectNulls
                                                                                opacity={meterVisibility.voltage_cn_v === false ? 0 : 1}
                                                                            />

                                                                            <Brush
                                                                                dataKey="recorded_at"
                                                                                height={20}
                                                                                stroke={darkMode ? colors.g4 : '#8884d8'}
                                                                                travellerWidth={10}
                                                                                tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                            />
                                                                        </ComposedChart>
                                                                    </ResponsiveContainer>
                                                                ) : (
                                                                    <Typography align="center" color="text.secondary">
                                                                        No Voltage Unbalance data.
                                                                    </Typography>
                                                                )}
                                                            </Box>

                                                            {/* Current Unbalance Graph */}
                                                            <Box sx={{ width: '48%', height: 350, mb: 6 }}>
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    sx={{
                                                                        mb: 1,
                                                                        fontWeight: 600,
                                                                        textAlign: 'center',
                                                                        color: darkMode ? colors.f2 : colors.f3
                                                                    }}
                                                                >
                                                                    Phase difference (A)
                                                                </Typography>
                                                                {processedothersDatacurrent?.length > 0 ? (
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <ComposedChart data={processedothersDatacurrent}>
                                                                            <CartesianGrid strokeDasharray="3 3" />

                                                                            <XAxis
                                                                                dataKey="recorded_at"
                                                                                ticks={meterCurrentTicks}
                                                                                interval={0}
                                                                                tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                                angle={-65}
                                                                                textAnchor="end"
                                                                                height={60}
                                                                            />

                                                                            <YAxis
                                                                                yAxisId="left"
                                                                                tickFormatter={(value) => value.toFixed(1)}
                                                                                domain={[minVoltage, maxVoltage + 5]}
                                                                                label={{
                                                                                    value: 'Current (A)',
                                                                                    angle: -90,
                                                                                    position: 'insideLeft',
                                                                                    style: { fontSize: '0.7vw' },
                                                                                }}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                            />

                                                                            <YAxis
                                                                                yAxisId="right"
                                                                                orientation="right"
                                                                                tickFormatter={(value) => value.toFixed(2)}
                                                                                domain={[0, maxUnbalancecurrent + 1]}
                                                                                label={{
                                                                                    value: 'Unbalanced (%)',
                                                                                    angle: -90,
                                                                                    position: 'insideRight',
                                                                                    style: { fontSize: '0.7vw' },
                                                                                }}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                            />

                                                                            <Tooltip
                                                                                formatter={(v, name) => [v.toFixed(2), name]}
                                                                                labelFormatter={(label) =>
                                                                                    `Time: ${moment(label).format('DD-MM-YYYY HH:mm')}`
                                                                                }
                                                                            />
                                                                            <Legend
                                                                                verticalAlign="bottom"
                                                                                height={36}
                                                                                wrapperStyle={{ fontSize: '0.7vw' }}
                                                                                onClick={(e) => SM_handleLegendClick(meter.serial_number, e.dataKey)}
                                                                                formatter={(value) => (value === 'Unbalanced' ? null : value)}
                                                                            />

                                                                            <Area
                                                                                yAxisId="right"
                                                                                type="monotone"
                                                                                dataKey="nema_unbalanced_pct"
                                                                                name="NEMA Unbalanced (%)"
                                                                                fill="#74b800"
                                                                                stroke="#74b800"
                                                                                strokeWidth={2}
                                                                                fillOpacity={0.3}
                                                                            />

                                                                            <Area
                                                                                yAxisId="right"
                                                                                type="monotone"
                                                                                dataKey="Unbalanced"
                                                                                name=""
                                                                                fill="red"
                                                                                stroke="red"
                                                                                strokeWidth={2}
                                                                                fillOpacity={0.5}
                                                                                connectNulls={false}
                                                                                legendType="none"
                                                                            />

                                                                            <Line
                                                                                yAxisId="left"
                                                                                type="monotone"
                                                                                dataKey="current_a_a"
                                                                                name="Current A (A)"
                                                                                stroke="#ff7300"
                                                                                strokeWidth={2}
                                                                                dot={false}
                                                                                connectNulls
                                                                                opacity={meterVisibility.current_a_a === false ? 0 : 1}
                                                                            />
                                                                            <Line
                                                                                yAxisId="left"
                                                                                type="monotone"
                                                                                dataKey="current_b_a"
                                                                                name="Current B (A)"
                                                                                stroke="#ffc658"
                                                                                strokeWidth={2}
                                                                                dot={false}
                                                                                connectNulls
                                                                                opacity={meterVisibility.current_b_a === false ? 0 : 1}
                                                                            />
                                                                            <Line
                                                                                yAxisId="left"
                                                                                type="monotone"
                                                                                dataKey="current_c_a"
                                                                                name="Current C (A)"
                                                                                stroke="#6394cc"
                                                                                strokeWidth={2}
                                                                                dot={false}
                                                                                connectNulls
                                                                                opacity={meterVisibility.current_c_a === false ? 0 : 1}
                                                                            />

                                                                            <Brush
                                                                                dataKey="recorded_at"
                                                                                height={20}
                                                                                stroke={darkMode ? colors.g4 : '#8884d8'}
                                                                                travellerWidth={10}
                                                                                tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                            />
                                                                        </ComposedChart>
                                                                    </ResponsiveContainer>
                                                                ) : (
                                                                    <Typography align="center" color="text.secondary">
                                                                        No Current Unbalance data.
                                                                    </Typography>
                                                                )}
                                                            </Box>

                                                            {/* Power Phase Unbalance graph */}
                                                            <Box sx={{ width: '48%', height: 350, mb: 6 }}>
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    sx={{
                                                                        mb: 1,
                                                                        fontWeight: 600,
                                                                        textAlign: 'center',
                                                                        color: darkMode ? colors.f2 : colors.f3
                                                                    }}
                                                                >
                                                                    Phase difference (kW)
                                                                </Typography>
                                                                {processedothersDatapower?.length > 0 ? (
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <ComposedChart data={processedothersDatapower}>
                                                                            <CartesianGrid strokeDasharray="3 3" />

                                                                            <XAxis
                                                                                dataKey="recorded_at"
                                                                                ticks={meterPowerTicks}
                                                                                interval={0}
                                                                                tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                                angle={-65}
                                                                                textAnchor="end"
                                                                                height={60}
                                                                            />

                                                                            <YAxis
                                                                                yAxisId="left"
                                                                                domain={[minVoltage, maxVoltage + 5]}
                                                                                label={{
                                                                                    value: 'Active Power (kW)',
                                                                                    angle: -90,
                                                                                    position: 'insideLeft',
                                                                                    style: { fontSize: '0.6vw' },
                                                                                }}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                            />

                                                                            <YAxis
                                                                                yAxisId="right"
                                                                                orientation="right"
                                                                                domain={[0, maxUnbalancepower + 1]}
                                                                                label={{
                                                                                    value: 'Unbalanced (%)',
                                                                                    angle: -90,
                                                                                    position: 'insideRight',
                                                                                    style: { fontSize: '0.6vw' },
                                                                                }}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                            />

                                                                            <Tooltip
                                                                                formatter={(value, name) => [value, name]}
                                                                                labelFormatter={(label) => `Time: ${moment(label).format('DD-MM-YYYY HH:mm')}`}
                                                                            />
                                                                            <Legend
                                                                                verticalAlign="bottom"
                                                                                height={36}
                                                                                wrapperStyle={{ fontSize: '0.7vw' }}
                                                                                onClick={(e) => SM_handleLegendClick(meter.serial_number, e.dataKey)}
                                                                                formatter={(value) => (value === 'Unbalanced' ? null : value)}
                                                                            />

                                                                            <Line
                                                                                yAxisId="left"
                                                                                type="monotone"
                                                                                dataKey="active_power_a_kw"
                                                                                name="Active Power A (kW)"
                                                                                stroke="#ff7300"
                                                                                strokeWidth={2}
                                                                                dot={false}
                                                                                connectNulls
                                                                                opacity={power.active_power_a_kw === false ? 0 : 1}
                                                                            />
                                                                            <Line
                                                                                yAxisId="left"
                                                                                type="monotone"
                                                                                dataKey="active_power_b_kw"
                                                                                name="Active Power B (kW)"
                                                                                stroke="#ffc658"
                                                                                strokeWidth={2}
                                                                                dot={false}
                                                                                connectNulls
                                                                                opacity={power.active_power_b_kw === false ? 0 : 1}
                                                                            />
                                                                            <Line
                                                                                yAxisId="left"
                                                                                type="monotone"
                                                                                dataKey="active_power_c_kw"
                                                                                name="Active Power C (kW)"
                                                                                stroke="#6394cc"
                                                                                strokeWidth={2}
                                                                                dot={false}
                                                                                connectNulls
                                                                                opacity={power.active_power_c_kw === false ? 0 : 1}
                                                                            />

                                                                            <Brush
                                                                                dataKey="recorded_at"
                                                                                height={20}
                                                                                stroke={darkMode ? colors.g4 : '#8884d8'}
                                                                                travellerWidth={10}
                                                                                tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                            />
                                                                        </ComposedChart>
                                                                    </ResponsiveContainer>
                                                                ) : (
                                                                    <Typography align="center" color="text.secondary">
                                                                        No Power Phase Unbalance data.
                                                                    </Typography>
                                                                )}
                                                            </Box>

                                                            {/* Power Factor Graph */}
                                                            <Box sx={{ width: '48%', height: 350, mb: 6 }}>
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    sx={{
                                                                        mb: 1,
                                                                        fontWeight: 600,
                                                                        textAlign: 'center',
                                                                        color: darkMode ? colors.f2 : colors.f3
                                                                    }}
                                                                >
                                                                    Power Factor
                                                                </Typography>
                                                                {processedOthersPFData?.length > 0 ? (
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <LineChart data={processedOthersPFData}>
                                                                            <CartesianGrid strokeDasharray="3 3" />

                                                                            <XAxis
                                                                                dataKey="recorded_at"
                                                                                ticks={meterPFTicks}
                                                                                interval={0}
                                                                                tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                                angle={-65}
                                                                                textAnchor="end"
                                                                                height={60}
                                                                            />

                                                                            <YAxis
                                                                                domain={[0, 1]}
                                                                                tickFormatter={(value) => value.toFixed(2)}
                                                                                label={{
                                                                                    value: 'Power Factor (PF)',
                                                                                    angle: -90,
                                                                                    position: 'insideLeft',
                                                                                    style: { fontSize: '0.6vw' },
                                                                                }}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                            />

                                                                            <Tooltip
                                                                                formatter={(value) => [Number(value).toFixed(3), 'Power Factor']}
                                                                                labelFormatter={(label) =>
                                                                                    `Time: ${moment(label).format('DD-MM-YYYY HH:mm')}`
                                                                                }
                                                                            />

                                                                            <Legend
                                                                                verticalAlign="bottom"
                                                                                height={36}
                                                                            />

                                                                            <Line
                                                                                type="monotone"
                                                                                dataKey="total_pf"
                                                                                name="Power Factor"
                                                                                stroke={darkMode ? colors.g1 : colors.g8}
                                                                                strokeWidth={2}
                                                                                dot={false}
                                                                                connectNulls
                                                                            />

                                                                            <Area
                                                                                type="monotone"
                                                                                dataKey="low_pf"
                                                                                fill="red"
                                                                                stroke="red"
                                                                                fillOpacity={0.3}
                                                                                connectNulls={false}
                                                                            />

                                                                            <Brush
                                                                                dataKey="recorded_at"
                                                                                height={20}
                                                                                stroke={darkMode ? colors.g4 : '#8884d8'}
                                                                                travellerWidth={10}
                                                                                tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                            />
                                                                        </LineChart>
                                                                    </ResponsiveContainer>
                                                                ) : (
                                                                    <Typography align="center" color="text.secondary">
                                                                        No Power Factor data.
                                                                    </Typography>
                                                                )}
                                                            </Box>

                                                            {/* Apparent Power Graph */}
                                                            <Box sx={{ width: '48%', height: 350, mb: 6, }}>
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    sx={{
                                                                        mb: 1,
                                                                        fontWeight: 600,
                                                                        textAlign: 'center',
                                                                        color: darkMode ? colors.f2 : colors.f3
                                                                    }}
                                                                >
                                                                    Apparent Power
                                                                </Typography>
                                                                {otherKVAdata?.length > 0 ? (
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <LineChart data={otherKVAdata}>
                                                                            <CartesianGrid strokeDasharray="3 3" />

                                                                            <XAxis
                                                                                dataKey="recorded_at"
                                                                                ticks={meterKVATicks}
                                                                                interval={0}
                                                                                tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                                angle={-65}
                                                                                textAnchor="end"
                                                                                height={60}
                                                                            />

                                                                            <YAxis
                                                                                tickFormatter={(value) => value.toFixed(2)}
                                                                                label={{
                                                                                    value: ' Apparent Power',
                                                                                    angle: -90,
                                                                                    position: 'insideLeft',
                                                                                    style: { fontSize: '0.6vw' },
                                                                                }}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                            />

                                                                            <Tooltip
                                                                                formatter={(value) => [Number(value).toFixed(2), ' Apparent Power']}
                                                                                labelFormatter={(label) =>
                                                                                    `Time: ${moment(label).format('DD-MM-YYYY HH:mm')}`
                                                                                }
                                                                            />

                                                                            <Legend
                                                                                verticalAlign="bottom"
                                                                                height={36}
                                                                            />

                                                                            <Line
                                                                                type="monotone"
                                                                                dataKey="total_apparent_power_kva"
                                                                                name=" Apparent Power"
                                                                                stroke={darkMode ? colors.g1 : colors.g8}
                                                                                strokeWidth={2}
                                                                                dot={false}
                                                                                connectNulls
                                                                            />

                                                                            <Brush
                                                                                dataKey="recorded_at"
                                                                                height={20}
                                                                                stroke={darkMode ? colors.g4 : '#8884d8'}
                                                                                travellerWidth={10}
                                                                                tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                            />
                                                                        </LineChart>
                                                                    </ResponsiveContainer>
                                                                ) : (
                                                                    <Typography align="center" color="text.secondary">
                                                                        No Apparent Power data.
                                                                    </Typography>
                                                                )}
                                                            </Box>

                                                            {/* Building Load Percentage Graph */}
                                                            <Box sx={{ width: '48%', height: 350, mb: 6, }}>
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    sx={{
                                                                        mb: 1,
                                                                        fontWeight: 600,
                                                                        textAlign: 'center',
                                                                        color: darkMode ? colors.f2 : colors.f3
                                                                    }}
                                                                >
                                                                    Building Load Percentage
                                                                </Typography>

                                                                {meter.percentage_graph && meter.percentage_graph.length > 0 ? (
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <LineChart data={meter.percentage_graph}>  {/* Use raw data directly */}
                                                                            <CartesianGrid strokeDasharray="3 3" />

                                                                            <XAxis
                                                                                dataKey="recorded_at"
                                                                                ticks={meterOthersTicks}
                                                                                interval={0}
                                                                                tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                                angle={-65}
                                                                                textAnchor="end"
                                                                                height={60}
                                                                            />
                                                                            <YAxis
                                                                                domain={[0, 100]}  // Percentage is 0-100
                                                                                tickFormatter={(value) => {
                                                                                    // Format to 2 decimal places, but trim trailing .00
                                                                                    const formatted = Number(value).toFixed(2);
                                                                                    const trimmed = formatted.replace(/\.00$/, '');
                                                                                    return `${trimmed}%`;
                                                                                }}
                                                                                label={{
                                                                                    value: 'Percentage (%)',
                                                                                    angle: -90,
                                                                                    position: 'insideLeft',
                                                                                    style: { fontSize: '0.6vw' },
                                                                                }}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                            />

                                                                            <Tooltip
                                                                                formatter={(value) => [`${Number(value).toFixed(2)}%`, 'Building Load']}
                                                                                labelFormatter={(label) => `Time: ${moment(label).format('DD-MM-YYYY HH:mm')}`}
                                                                            />

                                                                            <Legend verticalAlign="bottom" height={36} />

                                                                            <Line
                                                                                type="monotone"
                                                                                dataKey="percentage"  // Use the percentage field from your data
                                                                                name="Building Load Percentage"
                                                                                stroke={darkMode ? colors.g1 : colors.g8}
                                                                                strokeWidth={2}
                                                                                dot={false}
                                                                                connectNulls
                                                                            />

                                                                            <Brush
                                                                                dataKey="recorded_at"
                                                                                height={20}
                                                                                stroke={darkMode ? colors.g4 : '#8884d8'}
                                                                                travellerWidth={10}
                                                                                tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                            />
                                                                        </LineChart>
                                                                    </ResponsiveContainer>
                                                                ) : (
                                                                    <Typography align="center" color="text.secondary">
                                                                        No Percentage data available.
                                                                    </Typography>
                                                                )}
                                                            </Box>

                                                            {/* Voltage Harmonics Graph */}
                                                            {hasHarmonicsData(processedHarmonicsVoltage, ['harmonic_voltage_rate_a', 'harmonic_voltage_rate_b', 'harmonic_voltage_rate_c']) && (
                                                                <Box sx={{ width: '48%', height: 350, mb: 6 }}>
                                                                    <Typography
                                                                        variant="subtitle1"
                                                                        sx={{
                                                                            mb: 1,
                                                                            fontWeight: 600,
                                                                            textAlign: 'center',
                                                                            color: darkMode ? colors.f2 : colors.f3
                                                                        }}
                                                                    >
                                                                        Voltage Harmonics (%)
                                                                    </Typography>
                                                                    {processedHarmonicsVoltage?.length > 0 ? (
                                                                        <ResponsiveContainer width="100%" height="100%">
                                                                            <ComposedChart data={processedHarmonicsVoltage}>
                                                                                <CartesianGrid strokeDasharray="3 3" />

                                                                                <XAxis
                                                                                    dataKey="recorded_at"
                                                                                    ticks={meterHarmonicsVoltageTicks}
                                                                                    interval={0}
                                                                                    tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                                    tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                                    angle={-65}
                                                                                    textAnchor="end"
                                                                                    height={60}
                                                                                />

                                                                                <YAxis
                                                                                    tickFormatter={(value) => value.toFixed(2)}
                                                                                    label={{
                                                                                        value: 'THD (%)',
                                                                                        angle: -90,
                                                                                        position: 'insideLeft',
                                                                                        style: { fontSize: '0.6vw' },
                                                                                    }}
                                                                                    tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                                />

                                                                                <Tooltip
                                                                                    formatter={(value, name) => [Number(value).toFixed(2), name]}
                                                                                    labelFormatter={(label) => `Time: ${moment(label).format('DD-MM-YYYY HH:mm')} `}
                                                                                />

                                                                                <Legend
                                                                                    verticalAlign="bottom"
                                                                                    height={36}
                                                                                    wrapperStyle={{ fontSize: '0.7vw' }}
                                                                                    onClick={(e) => SM_handleLegendClick(meter.serial_number, e.dataKey)}
                                                                                />

                                                                                <Line
                                                                                    type="monotone"
                                                                                    dataKey="harmonic_voltage_rate_a"
                                                                                    name="Voltage Harmonics A (%)"
                                                                                    stroke="#ff7300"
                                                                                    strokeWidth={2}
                                                                                    dot={false}
                                                                                    connectNulls
                                                                                    opacity={meterVisibility.harmonic_voltage_rate_a === false ? 0 : 1}
                                                                                />
                                                                                <Line
                                                                                    type="monotone"
                                                                                    dataKey="harmonic_voltage_rate_b"
                                                                                    name="Voltage Harmonics B (%)"
                                                                                    stroke="#ffc658"
                                                                                    strokeWidth={2}
                                                                                    dot={false}
                                                                                    connectNulls
                                                                                    opacity={meterVisibility.harmonic_voltage_rate_b === false ? 0 : 1}
                                                                                />
                                                                                <Line
                                                                                    type="monotone"
                                                                                    dataKey="harmonic_voltage_rate_c"
                                                                                    name="Voltage Harmonics C (%)"
                                                                                    stroke="#6394cc"
                                                                                    strokeWidth={2}
                                                                                    dot={false}
                                                                                    connectNulls
                                                                                    opacity={meterVisibility.harmonic_voltage_rate_c === false ? 0 : 1}
                                                                                />

                                                                                <Brush
                                                                                    dataKey="recorded_at"
                                                                                    height={20}
                                                                                    stroke={darkMode ? colors.g4 : '#8884d8'}
                                                                                    travellerWidth={10}
                                                                                    tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                                />
                                                                            </ComposedChart>
                                                                        </ResponsiveContainer>
                                                                    ) : (
                                                                        <Typography align="center" color="text.secondary">
                                                                            No Voltage Harmonics data.
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            )}

                                                            {/* Current Harmonics Graph */}
                                                            {hasHarmonicsData(processedHarmonicsCurrent, ['harmonic_current_rate_a', 'harmonic_current_rate_b', 'harmonic_current_rate_c']) && (
                                                                <Box sx={{ width: '48%', height: 350, mb: 6 }}>
                                                                    <Typography
                                                                        variant="subtitle1"
                                                                        sx={{
                                                                            mb: 1,
                                                                            fontWeight: 600,
                                                                            textAlign: 'center',
                                                                            color: darkMode ? colors.f2 : colors.f3
                                                                        }}
                                                                    >
                                                                        Current Harmonics (%)
                                                                    </Typography>
                                                                    {processedHarmonicsCurrent?.length > 0 ? (
                                                                        <ResponsiveContainer width="100%" height="100%">
                                                                            <ComposedChart data={processedHarmonicsCurrent}>
                                                                                <CartesianGrid strokeDasharray="3 3" />

                                                                                <XAxis
                                                                                    dataKey="recorded_at"
                                                                                    ticks={meterHarmonicsCurrentTicks}
                                                                                    interval={0}
                                                                                    tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                                    tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                                    angle={-65}
                                                                                    textAnchor="end"
                                                                                    height={60}
                                                                                />

                                                                                <YAxis
                                                                                    tickFormatter={(value) => value.toFixed(2)}
                                                                                    label={{
                                                                                        value: 'THD (%)',
                                                                                        angle: -90,
                                                                                        position: 'insideLeft',
                                                                                        style: { fontSize: '0.6vw' },
                                                                                    }}
                                                                                    tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                                />

                                                                                <Tooltip
                                                                                    formatter={(value, name) => [Number(value).toFixed(2), name]}
                                                                                    labelFormatter={(label) => `Time: ${moment(label).format('DD-MM-YYYY HH:mm')} `}
                                                                                />

                                                                                <Legend
                                                                                    verticalAlign="bottom"
                                                                                    height={36}
                                                                                    wrapperStyle={{ fontSize: '0.7vw' }}
                                                                                    onClick={(e) => SM_handleLegendClick(meter.serial_number, e.dataKey)}
                                                                                />

                                                                                <Line
                                                                                    type="monotone"
                                                                                    dataKey="harmonic_current_rate_a"
                                                                                    name="Current Harmonics A (%)"
                                                                                    stroke="#ff7300"
                                                                                    strokeWidth={2}
                                                                                    dot={false}
                                                                                    connectNulls
                                                                                    opacity={meterVisibility.harmonic_current_rate_a === false ? 0 : 1}
                                                                                />
                                                                                <Line
                                                                                    type="monotone"
                                                                                    dataKey="harmonic_current_rate_b"
                                                                                    name="Current Harmonics B (%)"
                                                                                    stroke="#ffc658"
                                                                                    strokeWidth={2}
                                                                                    dot={false}
                                                                                    connectNulls
                                                                                    opacity={meterVisibility.harmonic_current_rate_b === false ? 0 : 1}
                                                                                />
                                                                                <Line
                                                                                    type="monotone"
                                                                                    dataKey="harmonic_current_rate_c"
                                                                                    name="Current Harmonics C (%)"
                                                                                    stroke="#6394cc"
                                                                                    strokeWidth={2}
                                                                                    dot={false}
                                                                                    connectNulls
                                                                                    opacity={meterVisibility.harmonic_current_rate_c === false ? 0 : 1}
                                                                                />

                                                                                <Brush
                                                                                    dataKey="recorded_at"
                                                                                    height={20}
                                                                                    stroke={darkMode ? colors.g4 : '#8884d8'}
                                                                                    travellerWidth={10}
                                                                                    tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                                />
                                                                            </ComposedChart>
                                                                        </ResponsiveContainer>
                                                                    ) : (
                                                                        <Typography align="center" color="text.secondary">
                                                                            No Current Harmonics data.
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            )}

                                                            {/* Daily Load Profile (Unified Meters Max) Graph */}
                                                            {unifiedBreakdown.length > 0 && (
                                                                <Box sx={{ width: '48%', height: 350, mb: 6, ml: '24%' }}>
                                                                    <Typography
                                                                        variant="subtitle1"
                                                                        sx={{
                                                                            mb: 1,
                                                                            fontWeight: 600,
                                                                            textAlign: 'center',
                                                                            color: darkMode ? colors.f2 : colors.f3
                                                                        }}
                                                                    >
                                                                        Daily Load Profile (Hourly)
                                                                    </Typography>
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <ComposedChart data={unifiedBreakdown}>
                                                                            <CartesianGrid strokeDasharray="3 3" />

                                                                            <XAxis
                                                                                dataKey="period"
                                                                                ticks={meterUnifiedTicks}
                                                                                interval={0}
                                                                                tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                                angle={-65}
                                                                                textAnchor="end"
                                                                                height={60}
                                                                            />

                                                                            <YAxis
                                                                                yAxisId="left"
                                                                                domain={[minUnifiedPower, maxUnifiedPower + 0.5]}
                                                                                label={{
                                                                                    value: 'Power (kW)',
                                                                                    angle: -90,
                                                                                    position: 'insideLeft',
                                                                                    style: { fontSize: '0.7vw' },
                                                                                }}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                            />

                                                                            <YAxis
                                                                                yAxisId="right"
                                                                                orientation="right"
                                                                                domain={[0, Number((maxUnifiedPct + 1).toFixed(2))]}
                                                                                tickFormatter={(value) => Number(value).toFixed(2)}
                                                                                label={{
                                                                                    value: 'Load (%)',
                                                                                    angle: -90,
                                                                                    position: 'insideRight',
                                                                                    style: { fontSize: '0.7vw' },
                                                                                }}
                                                                                tick={{ fontSize: 12, fill: darkMode ? colors.f2 : colors.f3 }}
                                                                            />

                                                                            <Tooltip
                                                                                formatter={(v, name) => [typeof v === 'number' ? v.toFixed(2) : v, name]}
                                                                                labelFormatter={(label) =>
                                                                                    `Time: ${moment(label).format('DD-MM-YYYY HH:mm')}`
                                                                                }
                                                                            />
                                                                            <Legend
                                                                                verticalAlign="bottom"
                                                                                height={36}
                                                                                wrapperStyle={{ fontSize: '0.7vw' }}
                                                                            />

                                                                            <Area
                                                                                yAxisId="left"
                                                                                type="monotone"
                                                                                dataKey="power_max"
                                                                                name="Max Power (kW)"
                                                                                fill="#74b800"
                                                                                stroke="#74b800"
                                                                                strokeWidth={2}
                                                                                fillOpacity={0.3}
                                                                                connectNulls
                                                                            />

                                                                            <Line
                                                                                yAxisId="left"
                                                                                type="monotone"
                                                                                dataKey="power_avg"
                                                                                name="Avg Power (kW)"
                                                                                stroke="#ff7300"
                                                                                strokeWidth={2}
                                                                                dot={false}
                                                                                connectNulls
                                                                            />

                                                                            <Line
                                                                                yAxisId="left"
                                                                                type="monotone"
                                                                                dataKey="power_min"
                                                                                name="Min Power (kW)"
                                                                                stroke="#6394cc"
                                                                                strokeWidth={2}
                                                                                dot={false}
                                                                                connectNulls
                                                                            />

                                                                            <Line
                                                                                yAxisId="right"
                                                                                type="monotone"
                                                                                dataKey="pct_avg"
                                                                                name="Avg Load (%)"
                                                                                stroke="#ffc658"
                                                                                strokeWidth={2}
                                                                                dot={false}
                                                                                connectNulls
                                                                            />

                                                                            <Brush
                                                                                dataKey="period"
                                                                                height={20}
                                                                                stroke={darkMode ? colors.g4 : '#8884d8'}
                                                                                travellerWidth={10}
                                                                                tickFormatter={(tick) => moment(tick).format('HH:mm')}
                                                                            />
                                                                        </ComposedChart>
                                                                    </ResponsiveContainer>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Box>

                                            )}

                                        </Box>
                                    );
                                })}
                            </Box>
                        ) : (
                            ""
                        )}
                    </div>
                </Box>
            )}


        </>
    );
};



export default IndividualSection;
